import { PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Statistic, Typography, message } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAsyncEffect } from "use-async-effect";
import shopService from "../../../services/shop.service";
import { LayoutContext } from "../../providers/layout";
import ShopTable from "../../tables/shop-table";

interface ShopsProps {}

const Shops: React.FC<ShopsProps> = () => {
  const { setTitle, setLoading } = useContext(LayoutContext);
  const [shops, setShops] = useState<any[]>([]);
  const [reload, setReload] = useState(false);

  useEffect(() => {
    setTitle("Shops");
  }, [setTitle]);

  useAsyncEffect(async () => {
    setLoading(true);
    try {
      const whappoScopedShops = await shopService.getAllShops();
      const visibleShops = whappoScopedShops.filter(
        (shop: any) => shop.scope !== "whappo-deactive",
      );
      setShops(visibleShops);
    } finally {
      setLoading(false);
    }
  }, [reload]);

  const activeShopCount = useMemo(
    () => shops.filter((shop) => shop.isActive).length,
    [shops],
  );

  const inactiveShopCount = useMemo(
    () => shops.filter((shop) => !shop.isActive).length,
    [shops],
  );

  const updateShopActive = async (shopId: string, status: boolean) => {
    setLoading(true);
    try {
      const res = await shopService.updateShopActiveStatus(shopId, status);

      if (res && res.success === false) {
        message.error("Shop status could not be updated");
        return;
      }

      setReload((prev) => !prev);
      message.success("Shop status updated");
    } catch {
      message.error("Shop status could not be updated");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShop = async (shopId: string) => {
    setLoading(true);
    try {
      const res = await shopService.deleteShop(shopId);

      if (res && res.success === false) {
        message.error("Shop could not be deleted");
        return;
      }

      setReload((prev) => !prev);
      message.success("Shop deleted");
    } catch {
      message.error("Shop could not be deleted");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <Card
        bordered={false}
        style={{
          borderRadius: 24,
          background:
            "linear-gradient(135deg, #f8fafc 0%, #ffffff 45%, #f5f7fb 100%)",
          boxShadow:
            "0 10px 30px rgba(15, 23, 42, 0.06), 0 2px 10px rgba(15, 23, 42, 0.04)",
        }}
        bodyStyle={{ padding: 24 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Typography.Text
              style={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.4,
                color: "#667085",
                textTransform: "uppercase",
              }}
            >
              Shop management
            </Typography.Text>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 8,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 14,
                  display: "grid",
                  placeItems: "center",
                  background:
                    "linear-gradient(135deg, rgba(22,119,255,0.14), rgba(22,119,255,0.06))",
                  color: "#1677ff",
                }}
              >
                <ShopOutlined style={{ fontSize: 18 }} />
              </div>

              <div>
                <Typography.Title
                  level={2}
                  style={{ margin: 0, fontSize: 28, lineHeight: 1.15 }}
                >
                  Shops
                </Typography.Title>
                <Typography.Text style={{ color: "#667085", fontSize: 14 }}>
                  Manage stores, statuses and order access in one place.
                </Typography.Text>
              </div>
            </div>
          </div>

          <Link to="/shops/new">
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              style={{
                height: 44,
                borderRadius: 14,
                paddingInline: 18,
                fontWeight: 600,
                boxShadow: "0 8px 20px rgba(22,119,255,0.22)",
              }}
            >
              Add new shop
            </Button>
          </Link>
        </div>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                background: "#ffffffcc",
                boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Statistic title="Total shops" value={shops.length} />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                background: "#ffffffcc",
                boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Statistic title="Active shops" value={activeShopCount} />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 20,
                background: "#ffffffcc",
                boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Statistic title="Inactive shops" value={inactiveShopCount} />
            </Card>
          </Col>
        </Row>
      </Card>

      <ShopTable
        data={shops}
        updateShopActive={updateShopActive}
        onDeleteShop={handleDeleteShop}
      />
    </div>
  );
};

export default Shops;
